import type { CommunityStructure, MyCommunity } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { queryClient } from "../../lib/queryClient";
import { confirm } from "../../components/ui/Dialog";
import { toast } from "../../components/ui/Toast";
import { copyText } from "../posts/usePostActions";

/** POST /api/me/communities/read, then clear their unread state in the cache. */
export async function markCommunitiesRead(ids: string[]) {
  if (!ids.length) return;
  const set = new Set(ids);
  try {
    await api.post("/api/me/communities/read", { ids });
    queryClient.setQueryData<MyCommunity[]>(["my-communities"], (old) => old?.map((c) => (set.has(c.id) ? { ...c, unread: false, mentionCount: 0 } : c)));
    for (const id of ids)
      queryClient.setQueryData<CommunityStructure>(["structure", id], (old) => old && { ...old, channels: old.channels.map((ch) => ({ ...ch, unread: false, mentionCount: 0 })) });
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  } catch (e) {
    toast.error(errorMessage(e, "Couldn't mark as read"));
  }
}

export async function copyCommunityLink(c: Pick<MyCommunity, "id" | "slug" | "visibility">) {
  // Public communities have a shareable page; private ones link straight in (members only).
  await copyText(c.visibility === "public" ? `${window.location.origin}/community/${c.slug}` : `${window.location.origin}/c/${c.id}`);
  toast.show("Link copied");
}

/** Same flow as the community header menu. Returns true once left. */
export async function leaveCommunity(c: Pick<MyCommunity, "id" | "name" | "visibility">, navigate: (to: string) => void) {
  const ok = await confirm({ title: `Leave ${c.name}?`, description: c.visibility === "private" ? "You'll need a new invite to rejoin." : undefined, confirmLabel: "Leave", danger: true });
  if (!ok) return false;
  try {
    await api.post(`/api/communities/${c.id}/leave`);
    queryClient.setQueryData<MyCommunity[]>(["my-communities"], (old) => old?.filter((x) => x.id !== c.id));
    await queryClient.invalidateQueries({ queryKey: ["my-communities"] });
    queryClient.removeQueries({ queryKey: ["structure", c.id] });
    if (window.location.pathname.startsWith(`/c/${c.id}`)) navigate("/c");
    return true;
  } catch (e) {
    toast.error(errorMessage(e));
    return false;
  }
}
